import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Unity, useUnityContext } from "react-unity-webgl";
// import { useTranslation } from "react-i18next";
import { useAuth } from "../../../hooks/useAuth";
import { axiosPrivate } from "../../../api/axios";
import API_URL from "../../../utils/API_URL";
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import { setDisasterStatus } from "../../../slice/authSlice";
import {
  PictureAsPdf as PdfIcon,
  FiberManualRecord as DotIcon
} from "@mui/icons-material";

import TPCSidebar from "./TPCSidebar";
import TPCDashboard from "./TPCDashboard";
import { TopKpiSlider } from "./KPI";
import SOPChecklist from "./SOPChecklist";
import MainteSOPChecklist from "./MainteSOPChecklist";
import MachineDetailInfo from "./MachineDetailInfo";
import DetailHistoryPopup from "./DetailHistoryPopup";

const extractPickingTypes = (payload) => {
  const types = new Set();
  const queue = [payload];
  const visited = new WeakSet();

  const addType = (value) => {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) {
      types.add(Math.trunc(asNumber));
    }
  };

  while (queue.length > 0) {
    const current = queue.shift();
    if (current == null) continue;

    if (typeof current === "string") {
      const typeRegex = /["']?type["']?\s*[:=]\s*["']?(-?\d+(?:\.\d+)?)/gi;
      let match = typeRegex.exec(current);
      while (match) {
        addType(match[1]);
        match = typeRegex.exec(current);
      }

      try {
        const parsed = JSON.parse(current);
        queue.push(parsed);
      } catch (e) {
        // Ignore malformed/non-JSON strings and keep regex-only extraction.
      }
      continue;
    }

    if (typeof current === "object") {
      if (visited.has(current)) continue;
      visited.add(current);

      if (!Array.isArray(current) && Object.prototype.hasOwnProperty.call(current, "type")) {
        addType(current.type);
      }

      if (Array.isArray(current)) {
        current.forEach((item) => queue.push(item));
      } else {
        Object.values(current).forEach((value) => queue.push(value));
      }
    }
  }

  return types;
};

export default function TPCPage() {
  // TODO: 언어 모드 임시 해제
  // const { t } = useTranslation();
  const auth = useAuth();
  const dispatch = useDispatch();
  const { group_code, project_code } = useParams();
  const unityContainerRef = useRef(null);
  const socketRef = useRef(null);

  const { graphStatus, disasterStatus, kpiStatus } = useSelector((state) => state.auth);
  const userInfo = useSelector((state) => state.auth.user);

  // Unity Context
  const { unityProvider, unload, sendMessage, isLoaded, loadingProgression } = useUnityContext({
    loaderUrl: "https://preview.insightwin.com/Build/WebGL.loader.js",
    dataUrl: "https://preview.insightwin.com/Build/WebGL.data.gz",
    frameworkUrl: "https://preview.insightwin.com/Build/WebGL.framework.js.gz",
    codeUrl: "https://preview.insightwin.com/Build/WebGL.wasm.gz",
    streamingAssetsUrl: `https://preview.insightwin.com/StreamingAssets/${group_code}/${project_code}/le`,
  });

  // Tailwind CDN Dynamic Loading (Scoped to this page lifecycle)
  const [isTailwindReady, setIsTailwindReady] = useState(false);
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.tailwindcss.com";
    script.id = "tailwind-cdn-script";
    script.onload = () => {
      // Small timeout to ensure Tailwind has processed the DOM
      setTimeout(() => setIsTailwindReady(true), 100);
    };
    document.head.appendChild(script);

    return () => {
      // Remove the script tag
      const existingScript = document.getElementById("tailwind-cdn-script");
      if (existingScript) existingScript.remove();

      // Remove style tags injected by Tailwind Play CDN to prevent global style leakage
      const tailwindStyles = document.querySelectorAll('style');
      tailwindStyles.forEach(style => {
        if (style.textContent.includes('--tw-') || style.textContent.includes('tailwindcss')) {
          style.remove();
        }
      });
    };
  }, []);

  // 상태 관리
  // 시스템 활성화 상태 관리
  const [systemStatus, setSystemStatus] = useState({ APS: false, SAFE: false });
  // 선택한 장비 ID
  const [currentMchId, setCurrentMchId] = useState("");
  // 장비 세부 정보창 열림/닫힘 상태
  const [mchInfoOpen, setMchInfoOpen] = useState(false);
  // 장비 세부 정보
  const [mchInfoData, setMchInfoData] = useState(null);
  // 재난 SOP 상태
  const [disasterScenario, setDisasterScenario] = useState(null);
  // 설비 보전 SOP 상태
  const [mainteSOPOpen, setMainteSOPOpen] = useState(false);

  // History Popup 상태
  const [historyPopupOpen, setHistoryPopupOpen] = useState(false);
  const [historyPopupConfig, setHistoryPopupConfig] = useState({ title: '', columns: [], data: [] });

  // Unity Cleanup
  useEffect(() => {
    return () => {
      const cleanup = async () => {
        try {
          await unload();
          console.log("Unity unloaded");
        } catch (e) {
          console.warn("Unity unload error", e);
        }
      };
      if (isLoaded) cleanup();
    };
  }, [unload, isLoaded]);

  // 언어 변경 모드 미사용으로 기본 한국어 모드 설정
  useEffect(() => {
    if (isLoaded) {
      sendMessage("Canvas", "SetPropertyValue", "Language:KOR");
    }
  }, [isLoaded, sendMessage]);

  // Window Resize Handling for Unity Canvas
  const [devicePixelRatio] = useState(window.devicePixelRatio);

  const updateCanvasSize = useCallback(() => {
    if (unityContainerRef.current) {
      const canvas = unityContainerRef.current.querySelector("canvas");
      if (canvas) {
        const { clientWidth, clientHeight } = unityContainerRef.current;
        canvas.width = clientWidth * window.devicePixelRatio;
        canvas.height = clientHeight * window.devicePixelRatio;
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', updateCanvasSize);
    updateCanvasSize();
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [updateCanvasSize]);

  // 시스템 활성화 상태 조회
  useEffect(() => {
    const fetchSystemStatus = async () => {
      try {
        const response = await axiosPrivate.post("/project/getSystemStatus", {
          group_code,
          project_code,
        });
        setSystemStatus(response.data);
      } catch (error) {
        console.error("시스템 상태 조회 실패:", error);
      }
    };
    fetchSystemStatus();
  }, [group_code, project_code]);

  // 로그아웃 핸들러
  const handleLogout = async () => {
    await unload();
    await auth.logout();
  };

  const handleDtInfoModeChange = (mode) => {
    sendMessage("Canvas", "SetPropertyValue", `InsighTwinMode:${mode}`);
  };

  // 재난 상황 핸들러 (시작/종료)
  const handleFireAlarm = useCallback(async () => {
    if (!systemStatus.SAFE) return;

    if (disasterStatus.isActive) {
      // 상황 해제 요청
      try {
        await axiosPrivate.post("/scenario/resolve", {
          group_code,
          project_code,
        });
      } catch (error) {
        toast.error(error.response?.data || "상황 종료에 실패했습니다.");
      }
    } else {
      // 상황 시작 요청 (화재 시나리오 고정: scenario_category: 1)
      try {
        await axiosPrivate.post("/scenario/start", {
          group_code,
          project_code,
          scenario_category: 1,
        });
      } catch (error) {
        toast.error(error.response?.data || "시나리오 시작에 실패했습니다.");
      }
    }
  }, [systemStatus.SAFE, disasterStatus.isActive, group_code, project_code]);

  // 체크리스트 항목 클릭 핸들러
  const handleChecklistItem = useCallback((step_id, new_status) => {
    if (!userInfo) {
      toast.error("사용자 정보가 로드되지 않았습니다.");
      return;
    }

    if (socketRef.current && disasterScenario) {
      socketRef.current.emit("disaster:checkItem", {
        log_id: disasterScenario.log_id,
        step_id,
        is_checked: new_status,
        user_id: userInfo?.user_name || "Unknown",
        group_code,
        project_code,
      });
    }
  }, [userInfo, disasterScenario, group_code, project_code]);

  // Redux 상태 동기화 (disasterScenario 변경 시마다)
  useEffect(() => {
    if (disasterScenario) {
      const allChecked = disasterScenario.checklist.every(item => item.is_checked === 2);
      dispatch(setDisasterStatus({
        isActive: true,
        isAllChecked: allChecked
      }));
      // 화재 발생 시 보전 SOP는 강제 종료
      setMainteSOPOpen(false);
    } else {
      dispatch(setDisasterStatus({
        isActive: false,
        isAllChecked: false
      }));
    }
  }, [disasterScenario, dispatch]);

  // 장비 정보 데이터 가져오기
  const fetchMchInfoData = useCallback(async (mchId) => {
    try {
      const response = await axiosPrivate.post("/dt/getMachineTotalInfo", {
        mchId,
        group_code,
        project_code,
      });

      const { asset, riskData, brokenLogData } = response.data;

      setMchInfoData({
        ...asset,
        mchId, // Explicitly include the ID passed from Unity/API call
        riskData,
        brokenLogData
      });
      console.log(asset);
    } catch (error) {
      console.error("Error fetching data:", error);
      setMchInfoData(null);
    }
  }, [group_code, project_code]);

  // Unity에서 사용할 전역 함수
  // Unity가 로딩 시작과 끝났음을 알려주는 함수 (임시 비활성화)
  const SFV_FinishLoading = useCallback((msg) => {
    console.log(msg);
    if (msg === "Initialization start") {
      console.log("Initialization start");
      // 로딩 상태 관리 해제
      // setIsLoading(false);
    } else if (msg === "Initialization done") {
      sendMessage("Canvas", "SetFloorVisible", "10");
    }
  }, [sendMessage]);

  // 장비 ID와 피킹 데이터를 받아서 처리하는 함수
  const SFV_UpdateData = useCallback(
    (mchId, pickingData) => {
      console.log("MchID:", mchId, "PickingData:", pickingData);
      const hasMchId = mchId !== "" && mchId !== null && mchId !== undefined;
      const pickingTypes = extractPickingTypes(pickingData);
      const isType21 = pickingTypes.has(21) || pickingTypes.has(22);
      const isType0 = pickingTypes.has(0);
      const isType20 = pickingTypes.has(20);

      if (hasMchId && isType21) {
        setCurrentMchId(mchId);
        fetchMchInfoData(mchId);
        setMchInfoOpen(true);
        setMainteSOPOpen(false); // 장비 전환 시 기존 체크리스트 닫기
      }
      if (mchId === "1" && isType0) {
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(() => {
            console.log("마이크 권한 승인");
            sendMessage("Canvas", "CurrentMicrophoneStatus", "Enable");
          })
          .catch((error) => {
            console.error("마이크 권한 획득 실패: ", error);
            sendMessage("Canvas", "CurrentMicrophoneStatus", error.message);
          });
      }
      if (hasMchId && isType20) {
        console.log("창고 피킹");
        // 창고 모드 비활성화
        // setCurrentMchId(mchId);
        // setStockOpen(true);
      }
    },
    [sendMessage, fetchMchInfoData]
  );

  const SFV_UpdateStatus = useCallback((type, status) => {
    if (type === "Fire" && status === "Imitation") {
      console.log("모의 화재 발생 -> SOP 자동 시작");
      handleFireAlarm();
    }
    if (type === "Fire" && status === "Fin") {
      // console.log("모의 화재 종료 -> SOP 자동 해제");
      // handleFireAlarm();
    }
    console.log(type, status);
  }, [handleFireAlarm]);

  // Unity에서 사용할 전역 함수 등록
  useEffect(() => {
    window.SFV_UpdateData = SFV_UpdateData;
    window.SFV_UpdateStatus = SFV_UpdateStatus;
    window.SFV_FinishLoading = SFV_FinishLoading;
    return () => {
      delete window.SFV_UpdateData;
      delete window.SFV_UpdateStatus;
      delete window.SFV_FinishLoading;
    };
  }, [SFV_UpdateData, SFV_UpdateStatus, SFV_FinishLoading]);

  // 웹소켓 연결 및 이벤트 핸들러 설정
  useEffect(() => {
    if (!systemStatus.SAFE) return; // SAFE 미사용 시 웹소켓 연결 안 함

    if (!socketRef.current) {
      let socketUrl;
      try {
        const url = new URL(API_URL);
        socketUrl = url.origin;
      } catch (error) {
        socketUrl = window.location.origin;
      }
      const socket = io(socketUrl + "/fire", {
        path: "/socket.io/",
        withCredentials: true,
        transports: ["websocket"],
      });
      socketRef.current = socket;

      socket.on("connect", () => {
        console.log("Fire alarm namespace connected");
        // 1. 구독
        socket.emit("subscribe", { group_code, project_code }, (response) => {
          console.log("Subscribed:", response);
          // 2. 구독 성공 후, 현재 재난 상태 요청
          socket.emit("disaster:getStatus", { group_code, project_code });
        });
      });

      socket.on("disaster:started", (data) => {
        console.log("Disaster started:", data);
        setDisasterScenario(data);
      });

      socket.on("disaster:itemUpdated", (update) => {
        console.log(
          "%c[WebSocket] 'disaster:itemUpdated' 이벤트 수신",
          "color: #00ddff; font-weight: bold;",
          update
        );
        setDisasterScenario((prev) => {
          console.log("%c[State Update] 이전 상태:", "color: #ff8c00;", prev);

          if (!prev || prev.log_id !== update.log_id) {
            console.warn(
              "[State Update] 업데이트 무시됨: log_id가 다르거나 이전 상태가 없음."
            );
            return prev;
          }

          const newChecklist = prev.checklist.map((item) => {
            if (item.step_id === update.step_id) {
              // 서버에서 받은 업데이트 내용 전체를 기존 아이템에 덮어쓰기
              return { ...item, ...update };
            }
            return item; // 변경 없는 아이템은 그대로 반환
          });

          // 불변성을 유지하며 새로운 상태 객체 생성
          const newState = { ...prev, checklist: newChecklist };
          console.log(
            "%c[State Update] 설정될 새로운 상태:",
            "color: #00ff00; font-weight: bold;",
            newState
          );
          return newState;
        });
      });

      socket.on("disaster:resolved", (data) => {
        console.log("Disaster resolved:", data);
        toast.success("재난 상황이 해제되었습니다.");
        setDisasterScenario(null);
      });

      socket.on("disconnect", () => {
        console.log("Fire alarm namespace disconnected");
      });
    }

    return () => {
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [group_code, project_code, systemStatus.SAFE]);

  // Click Outside Handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      // If Machine Detail Panel is not open, do nothing
      if (!mchInfoOpen) return;

      const target = e.target;

      // Allow interactions in floating/portalized controls that belong to machine detail UI
      if (target instanceof Element && target.closest('[data-machine-detail-safe-click="true"]')) {
        return;
      }

      const detailInfoPanel = document.getElementById('machine-detail-info');
      const sopChecklistPanel = document.getElementById('mainte-sop-checklist');

      // Check if click is inside the Machine Detail Info panel
      if (detailInfoPanel && detailInfoPanel.contains(target)) {
        return;
      }

      // Check if click is inside the active SOP Checklist panel (if it exists)
      if (sopChecklistPanel && sopChecklistPanel.contains(target)) {
        return;
      }
      
      // Check if click is inside the Detail History Popup (if it exists)
      const detailHistoryPopup = document.getElementById('detail-history-popup');
      if (detailHistoryPopup && detailHistoryPopup.contains(target)) {
        return;
      }

      // If click is outside both, close the Detail Info panel
      setMchInfoOpen(false);
      // Also close the Maintenance SOP Checklist if it's open (as per user request)
      if (mainteSOPOpen) {
        setMainteSOPOpen(false);
      }
    };

    // Use 'mousedown' to trigger before 'click' to capture the event early
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mchInfoOpen, mainteSOPOpen]);

  const loadingPercent = Math.round(loadingProgression * 100);

  return (
    <div className="flex w-full h-screen bg-black overflow-hidden relative font-['Pretendard']">
      {/* Sidebar (Fixed Width) */}
      <div className="shrink-0">
        <TPCSidebar
          handleLogout={handleLogout}
          onDtInfoModeChange={handleDtInfoModeChange}
          handleFireAlarm={handleFireAlarm}
          onOpenCertificate={() => {
            sendMessage("Canvas", "SetCaptureAllKeyboardInput", "false");
            setHistoryPopupConfig({
              title: "자율안전인증 현황",
              columns: certColumns,
              data: certData
            });
            setHistoryPopupOpen(true);
          }}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 relative" ref={unityContainerRef}>

        {/* Loading Overlay (Hidden only when both Unity and Tailwind are ready) */}
        {(!isLoaded || !isTailwindReady) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #0a0c10 0%, #12151c 50%, #0d1017 100%)',
              overflow: 'hidden',
            }}
          >
            {/* 배경 그리드 패턴 */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                linear-gradient(rgba(115,103,240,0.03) 1px, transparent 1px),
                linear-gradient(90deg, rgba(115,103,240,0.03) 1px, transparent 1px)
              `,
                backgroundSize: '40px 40px',
                opacity: 0.8,
              }}
            />

            {/* 글로우 효과 */}
            <div
              style={{
                position: 'absolute',
                width: '600px',
                height: '600px',
                background: 'radial-gradient(circle, rgba(115,103,240,0.12) 0%, transparent 70%)',
                borderRadius: '50%',
                filter: 'blur(60px)',
                animation: 'pulse 4s ease-in-out infinite',
              }}
            />

            {/* 로고 영역 */}
            <div style={{ position: 'relative', marginBottom: '48px', textAlign: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '12px' }}>
                <img src="/images/ese_logo.png" alt="ESE-TECH" style={{ width: '56px', height: 'auto', filter: 'drop-shadow(0 0 20px rgba(115,103,240,0.4))' }} />
                <span style={{
                  fontSize: '42px',
                  fontWeight: 800,
                  color: '#E1DEF5',
                  letterSpacing: '-0.02em',
                  textShadow: '0 0 40px rgba(115,103,240,0.3)',
                }}>
                  InsighTwin
                </span>
              </div>
              <p style={{
                color: 'rgba(225,222,245,0.5)',
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}>
                Digital Twin Platform
              </p>
            </div>

            {/* 프로그레스 바 컨테이너 */}
            <div style={{ position: 'relative', width: '320px' }}>
              {/* 프로그레스 바 배경 */}
              <div
                style={{
                  height: '6px',
                  background: 'rgba(47,51,73,0.6)',
                  borderRadius: '3px',
                  overflow: 'hidden',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
                }}
              >
                {/* 프로그레스 바 */}
                <div
                  style={{
                    width: `${loadingPercent}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #7367F0 0%, #9E95F5 50%, #7367F0 100%)',
                    backgroundSize: '200% 100%',
                    borderRadius: '3px',
                    transition: 'width 0.3s ease-out',
                    boxShadow: '0 0 20px rgba(115,103,240,0.5), 0 0 40px rgba(115,103,240,0.2)',
                    animation: 'shimmer 2s linear infinite',
                  }}
                />
              </div>

              {/* 퍼센트 표시 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '16px',
              }}>
                <span style={{
                  color: 'rgba(225,222,245,0.4)',
                  fontSize: '11px',
                  fontFamily: '"JetBrains Mono", "SF Mono", Consolas, monospace',
                  letterSpacing: '0.05em',
                }}>
                  LOADING ASSETS
                </span>
                <span style={{
                  color: '#7367F0',
                  fontSize: '14px',
                  fontWeight: 700,
                  fontFamily: '"JetBrains Mono", "SF Mono", Consolas, monospace',
                  textShadow: '0 0 10px rgba(115,103,240,0.5)',
                }}>
                  {loadingPercent}%
                </span>
              </div>
            </div>

            {/* CSS 애니메이션 */}
            <style>{`
            @keyframes pulse {
              0%, 100% { transform: scale(1); opacity: 0.5; }
              50% { transform: scale(1.1); opacity: 0.8; }
            }
            @keyframes shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>
          </div>
        )}

        {/* TPC Dashboard (Bottom Overlay) */}
        {graphStatus && <TPCDashboard />}

        {/* Top KPI Snapshot Slider */}
        {kpiStatus && <TopKpiSlider />}

        {/* SOP Checklist (Slide-in Overlay) */}
        {disasterScenario && (
          <SOPChecklist
            scenarioData={disasterScenario}
            onCheckItem={handleChecklistItem}
          />
        )}

        {/* Unity Canvas */}
        <Unity
          unityProvider={unityProvider}
          style={{ width: '100%', height: '100%' }}
          devicePixelRatio={devicePixelRatio}
        />

        {/* Maintenance SOP Checklist (Slide-in Overlay) */}
        <MainteSOPChecklist
          isOpen={mainteSOPOpen}
          onClose={() => setMainteSOPOpen(false)}
          machineName={mchInfoData?.machine_name}
        />

        {/* Machine Detail Info (Slide-in Right Overlay) */}
        <MachineDetailInfo
          isOpen={mchInfoOpen}
          onClose={() => {
            setMchInfoOpen(false);
            setMainteSOPOpen(false);
          }}
          data={mchInfoData}
          sendMessage={sendMessage}
          onOpenHistory={(config) => {
            sendMessage("Canvas", "SetCaptureAllKeyboardInput", "false");
            setHistoryPopupConfig(config);
            setHistoryPopupOpen(true);
          }}
          onStatusClick={() => setMainteSOPOpen(true)}
          onDownloadCert={handleDownloadCert}
        />

        {/* Global Detail History Popup (Centered over Unity) */}
        <DetailHistoryPopup
          isOpen={historyPopupOpen}
          onClose={() => {
            sendMessage("Canvas", "SetCaptureAllKeyboardInput", "true");
            setHistoryPopupOpen(false);
          }}
          {...historyPopupConfig}
        />
      </div>
    </div>
  );
}

// --- Demo Certificate Data & Columns ---
const handleDownloadCert = async (mchId) => {
  try {
    const response = await axiosPrivate.post("/dt/getCertificate",
      { mchId: mchId },
      { responseType: 'blob' }
    );
    const file = new Blob([response.data], { type: 'application/pdf' });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  } catch (error) {
    console.error("PDF download failed:", error);
    toast.error("증명서를 불러오는데 실패했습니다.");
  }
};

const certColumns = [
  {
    accessorKey: "status",
    header: "상태",
    cell: ({ getValue }) => {
      const val = getValue();
      const colorMap = {
        '유효': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
        '만료': 'bg-red-500/10 text-red-400 border-red-500/20',
        '미등록': 'bg-slate-500/10 text-slate-400 border-slate-500/20'
      };
      return (
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[10px] font-black uppercase tracking-wider ${colorMap[val] || colorMap['미등록']}`}>
          <DotIcon sx={{ fontSize: 8 }} />
          {val}
        </div>
      );
    }
  },
  {
    accessorKey: "mchName",
    header: "기계기구명",
  },
  {
    accessorKey: "manageNo",
    header: "관리번호",
  },
  {
    accessorKey: "expiryDate",
    header: "검사만료일",
  },
  {
    accessorKey: "cert",
    header: "증명서",
    cell: ({ row }) => {
      const hasCert = row.original.cert;
      if (!hasCert) return <span className="text-slate-700 text-[10px] font-black italic">NOT AVAILABLE</span>;
      return (
        <button
          onClick={() => handleDownloadCert(row.original.manageNo)}
          className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 hover:bg-indigo-500 hover:text-white transition-all active:scale-90"
        >
          <PdfIcon sx={{ fontSize: 18 }} />
        </button>
      );
    }
  }
];

const certData = [
  { status: '유효', mchName: '컨베이어', manageNo: 'CNC-001', expiryDate: '2026-12-31', cert: true },
  { status: '유효', mchName: '냉각타워', manageNo: 'CNC-002', expiryDate: '2026-10-15', cert: true },
  { status: '만료', mchName: '자동 포장기(2라인)', manageNo: 'MIL-001', expiryDate: '2025-01-01', cert: true },
  { status: '유효', mchName: '수평믹서2', manageNo: 'LSR-001', expiryDate: '2027-05-20', cert: true },
  { status: '미등록', mchName: '수평믹서1', manageNo: 'PRS-005', expiryDate: '-', cert: false },
];
